import React from 'react'
import Translation from '../global/Translation'
import { mapMeasure } from '../../helpers'

/**
 * Transformaciones de los buckets que devuelve Elastic para los filtros del catálogo (bucketsTransform
 * de SearchKit). Viven aquí para poder compartirlas entre el catálogo (`FiltersV2`) y la landing de
 * campañas (PromotionFilters) sin duplicarlas.
 *
 * Salieron tal cual de FiltersV2, con los this.props que usaban convertidos en argumentos.
 */

export const finishTransform = (filters, intl) => {
    if (!Array.isArray(filters)) return []

    return filters
        .filter(filter => {
            if (!filter.key) {
                return false
            }
            return true
        })
        .map(filter => {
            filter.title = intl.formatMessage({ id: filter.key }, {}, filter.key)
            return filter
        })
        .sort((a, b) => a.key.localeCompare(b.key))
}

export const commercialFormatTransform = (filters, hasImperialUnits) => {
    if (!Array.isArray(filters)) return []

    return filters
        .map(bucket => ({
            title: mapMeasure(bucket.key, hasImperialUnits),
            ...bucket
        }))
        .sort((a, b) => a.key.localeCompare(b.key))
}

export const familyTransform = (filters, intl) => {
    const MATERIAL_KEY_MAP = {
        GRANITO: 'granite',
        MARMOL: 'marble',
        TRAVERTINO: 'travertine',
        CUARCITA: 'quartzite',
        ONIX: 'onyx',
        CALIZA: 'limestone',
        ARENISCA: 'sandstone',
        GNEIS: 'gneiss',
        SOAPSTONE: 'soapstone',
    };
    return filters
        .map(filter => {
            const upperKey = filter.key.toUpperCase();
            const normalizedKey = MATERIAL_KEY_MAP[upperKey]
                || filter.key.toLowerCase().replace(/\s+/g, '_');

            const title = intl.messages[normalizedKey]
                ? intl.formatMessage({ id: normalizedKey })
                : filter.key;

            return Object.assign({}, filter, { title: title });
        })
        .sort((a, b) => a.title.localeCompare(b.title));
}

export const thicknessTransform = filters => {
    if (!Array.isArray(filters)) return []
    return filters
        .map(filter => {
            filter.title = filter.key.replace('.', ',') + ' cm'
            return filter
        })
        .sort((a, b) => parseFloat(a.key) - parseFloat(b.key))
}

export const brandTransform = (filters, selectedCenter) => {
    if (!Array.isArray(filters)) return []

    const typeMap = {
        SERVICES: <Translation id="operaciones" defaultMessage="Operaciones" />,
        DEKTON: selectedCenter === 7130 ? 'DKTN' : 'Dekton'
    }

    return filters.map(bucket => {
        return {
            title: typeMap[bucket.key] || bucket.key,
            ...bucket
        }
    })
}

export const colorSerieDescTransform = filters => {
    if (!Array.isArray(filters)) return []

    return filters
        .filter(bucket => bucket && bucket.key.trim() !== '')
        .map(bucket => ({
            title: bucket.key,
            ...bucket
        }))
        .sort((a, b) => a.key.localeCompare(b.key))
}

export const toneTransform = (filters, intl) => {
    if (!Array.isArray(filters)) return []

    const typeMap = {
        white: intl.formatMessage({ id: 'white' }),
        black: intl.formatMessage({ id: 'black' }),
        grey: intl.formatMessage({ id: 'grey' }),
        cream: intl.formatMessage({ id: 'cream' }),
        brown: intl.formatMessage({ id: 'brown' }),
        other: intl.formatMessage({ id: 'other' }),
        blue: intl.formatMessage({ id: 'blue' }),
        green: intl.formatMessage({ id: 'green' }),
        red: intl.formatMessage({ id: 'red' }),
        yellow: intl.formatMessage({ id: 'yellow' })
    }

    return filters
        .filter(bucket => bucket && bucket.key.trim() !== '')
        .map(bucket => {
            const translation = typeMap[bucket.key]
            const translatedTitle = translation || bucket.key

            return {
                title: translatedTitle,
                ...bucket
            }
        })
        .sort((a, b) => a.title.localeCompare(b.title))
}

export const zaddonTransform = filters => {
    if (!Array.isArray(filters)) return []

    const typeMap = {
        MAL: <Translation id="yes" defaultMessage="Yes" />,
        NO: <Translation id="no" defaultMessage="No" />
    }

    return filters
        .filter(bucket => bucket && (bucket.key === 'NO' || bucket.key === 'MAL'))
        .map(bucket => ({
            title: typeMap[bucket.key] || bucket.key,
            ...bucket
        }))
}

/**
 * El formato comercial está indexado en un campo por idioma (`commercialFormat_ES`, `commercialFormat_EN`...).
 */
export const getCommercialFormatFieldName = locale => {
    const LANG_TO_INDEX = {
        da: 'DA',
        de: 'DE',
        'en-US': 'EN',
        en: 'EN',
        es: 'ES',
        fi: 'FI',
        'fr-CA': 'FR',
        'en-CA': 'FR',
        fr: 'FR',
        it: 'IT',
        nl: 'NL',
        no: 'NO',
        pt: 'PT',
        pl: 'EN',
        sv: 'SV',
        tr: 'TR',
        ja: 'JA'
    }

    return `commercialFormat_${LANG_TO_INDEX[locale] || 'EN'}`
}
